'use client';
import type { ReadonlyURLSearchParams } from 'next/navigation';
import { useCallback, useContext } from 'react';
import { useClient } from '../../hooks/useClient';
import {
  SearchParamsContext,
  useSafeSearchParams,
} from '../../utils/useSafeSearchParams';

/**
 * The panel's shared URL plumbing: reading `cms_*` after hydration and writing
 * it back without a router.
 *
 * The sort, the filters and the reader's details all keep their state in the
 * URL, and each had grown its own copy of this — three identical `readParam`s
 * and three identical history writes, agreeing only for as long as nobody
 * touched them. Escaping a value or moving off `window.history` is one edit
 * here rather than three.
 */
export type PanelQueryParams = {
  /**
   * Reads one parameter. Safe to call during render; returns `null` before
   * hydration, because the server cannot read the query string without
   * dropping the page's static cache.
   */
  readParam: (name: string) => string | null;
  /**
   * A key that changes whenever any of `names` changes, for memoising work
   * derived from them. `null` until hydration.
   */
  paramsKey: string | null;
  /**
   * Applies a mutation and writes it back. Always replaces the history entry,
   * so Back leaves the page rather than stepping through every change
   * (CMS-1903 R10.6). A write that changes nothing is skipped.
   */
  write: (mutate: (params: URLSearchParams) => void) => void;
};

export const usePanelQueryParams = (
  names: readonly string[],
): PanelQueryParams => {
  const searchParams = useSafeSearchParams();
  // Read directly rather than through the throwing helper — every jsdom test
  // rendering a block bare has no provider.
  const setSearchParams = useContext(SearchParamsContext)?.setSearchParams;

  const readParam = useCallback(
    (name: string) =>
      searchParams?.get(name) ??
      new URLSearchParams(window.location.search).get(name),
    [searchParams],
  );

  // `useClient` re-invokes its callback on every render, so reading the values
  // directly would hand every dependent hook a new object each time.
  const paramsKey =
    useClient(() =>
      names.map((name) => `${name}=${readParam(name) ?? ''}`).join('&'),
    ) ?? null;

  const write = useCallback(
    (mutate: (params: URLSearchParams) => void) => {
      const params = new URLSearchParams(window.location.search);
      mutate(params);

      const query = params.toString();
      // Not `usePathname()`: it throws on the null router context in blocks
      // unit-tested without one. The hash is carried so an anchor survives.
      const { pathname, hash, search } = window.location;
      const url = `${pathname}${query ? `?${query}` : ''}${hash}`;
      const unchanged = url === `${pathname}${search}${hash}`;

      /**
       * Two rules:
       *
       * - **No history entry for a URL that did not change.** A commit that
       *   changes nothing must not push — the keyboard path has already
       *   replaced the URL by the time Enter arrives, and a duplicate entry
       *   would make the first Back a no-op. `useProductFilter`'s spec asserts
       *   nothing is written at all in that case.
       * - **Publish to the context regardless.** The URL and the context can
       *   disagree: the context is seeded by an observer the URL never goes
       *   through, so an unchanged URL is not unchanged state. Skipping this
       *   left a cleared sort reading as still applied — the sort's own clear,
       *   since removed with Clear All no longer touching the sort (CMS-1871).
       */
      if (!unchanged) window.history.replaceState(null, '', url);

      // Next's `useSearchParams` does not observe a manual history write, so
      // nothing else republishes it to the context the state derives from.
      setSearchParams?.(params as ReadonlyURLSearchParams);
    },
    [setSearchParams],
  );

  return { readParam, paramsKey, write };
};
